import React, { ReactNode, useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { isMobile } from 'react-device-detect';
import { Button, Layout, Menu, Badge, Flex, Tooltip } from 'antd';
import {
    SettingFilled,
    NotificationFilled,
    FolderOpenFilled,
    FileTextOutlined,
    MobileOutlined,
    LineChartOutlined,
    TeamOutlined,
    MessageOutlined,
    BuildOutlined,
    FormOutlined,
} from '@ant-design/icons';

import { isNewYearAroundUs } from '@extensions/utils';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { setCurrentMenuItem, cleanupStates, setRestartServiceRequired } from '@store/actions';
import { serverFetch } from '@flc/server';

import { IUserSession } from '@entities/user-session';

import Garland from '@core/controls/easter-eggs/garland/garland';

import { Permission, hasPermission } from '@enums/permission';
import {
    WarehouseIcon,
    AcceptanceIcon,
    LogisticsIcon,
    ImportTruckIcon,
    DeliveryTruckIcon,
    CartIcon,
    BoxesIcon,
    LoaderIcon,
    AuditReportIcon,
    TariffIcon,
    CustomerRateIcon,
    BillIcon,
    UsdIcon,
    AccountingIcon,
    MiniLogoIcon,
    MainLogoIcon,
    EmailIcon,
    TelegramIcon,
    MaxAppIcon,
} from '@icons/index';

import '@core/index.css';

const getDefaultOpenKey = (menuItem: string) => {
    switch (menuItem) {
        case 'import':
        case 'delivery':
        case 'importpreloading':
            return 'logistics';

        case 'acceptance':
        case 'extraconsignments':
        case 'remainings':
        case 'shipmentjournals':
            return 'warehouse';

        case 'balances':
        case 'bills':
        case 'tariffs':
        case 'waybills':
            return 'finances';

        case 'usersstatistic':
        case 'importstatistic':
            return 'statistics';

        case 'newnotification':
        case 'push':
        case 'sms':
        case 'emails':
        case 'telegram':
        case 'max':
            return 'notifications';

        case 'countries':
        case 'cities':
        case 'waypoints':
        case 'warehouses':
        case 'consignees':
        case 'packagetemplates':
        case 'tradingplatforms':
        case 'transporttariffs':
        case 'users':
        case 'counterparties':
        case 'contacts':
        case 'accounts':
        case 'debts':
        case 'items':
        case 'companies':
        case 'cargocarriers':
            return 'settings';

        default:
            return '';
    }
};

interface IMainLayout {
    appVersion: string | undefined;
}

const MainLayout = (props: IMainLayout) => {
    const { appVersion } = props;

    const { Header, Content, Sider } = Layout;

    const navigate = useNavigate();
    const location = useLocation();

    const d = useAppDispatch();
    const currentMenuItem = useAppSelector<string>((s) => s.currentMenuItem);
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const restartServiceRequired = useAppSelector<boolean>((s) => s.restartServiceRequired);

    const [defaultOpenKey, setDefaultOpenKey] = useState(getDefaultOpenKey(currentMenuItem));
    const [activeFeedbacksQty, setActiveFeedbacksQty] = useState<number | undefined>();
    const [newLoadingPlansQty, setNewLoadingPlansQty] = useState<number | undefined>();

    const [viewSetup] = useState<boolean>(hasPermission(userSession.permissions, Permission.ViewSetup));
    const [viewAudit] = useState<boolean>(
        hasPermission(userSession.permissions, Permission.ManageWarehouse) ||
            hasPermission(userSession.permissions, Permission.ManageTransportBill) ||
            hasPermission(userSession.permissions, Permission.ManageBalance),
    );

    const [siderCollapsed, setSiderCollapsed] = useState<boolean>(isMobile);

    function getItem(label: string | ReactNode, key: string, urlTo?: string, icon?: React.JSX.Element, children?: Array<any>) {
        const itemlabel = urlTo ? <Link to={urlTo}>{label}</Link> : label;

        return {
            key,
            icon,
            children,
            label: itemlabel,
        };
    }

    useEffect(() => {
        const items = location.pathname.replace('/flc', '').split('/');

        let key;
        switch (items[1]) {
            case 'logistics':
            case 'settings':
            case 'warehouse':
            case 'finance':
            case 'notifications':
            case 'statistics':
                key = items[2];
                break;
            default:
                key = items[1];
        }

        d(setCurrentMenuItem(key));

        getActiveFeedbacks();
        getNewLoadingPlans();

        const interval = setInterval(() => {
            getActiveFeedbacks();
            getNewLoadingPlans();
        }, 60000); //per 1 min

        return () => clearInterval(interval);
    }, [location]);

    useEffect(() => {
        getActiveFeedbacks();
    }, [restartServiceRequired]);

    const getActiveFeedbacks = async () => {
        d(setRestartServiceRequired(false));

        await serverFetch('feedbacks/active', { method: 'GET' })
            .then((data) => {
                setActiveFeedbacksQty(data);
            })
            .catch(() => {
                setActiveFeedbacksQty(undefined);
            });
    };

    const getNewLoadingPlans = async () => {
        d(setRestartServiceRequired(false));

        await serverFetch('loadingplans/new', { method: 'GET' })
            .then((data) => {
                setNewLoadingPlansQty(data);
            })
            .catch(() => {
                setNewLoadingPlansQty(undefined);
            });
    };

    const onLogout = () => {
        serverFetch(`auth/logout`, { method: 'POST' })
            .then(() => {
                d(cleanupStates());
                navigate('/');
            })
            .catch(() => {
                d(cleanupStates());
                navigate('/');
            });
    };

    useEffect(() => {
        setDefaultOpenKey(getDefaultOpenKey(currentMenuItem));
    }, [currentMenuItem]);

    const items = [
        getItem('Логистика', 'logistics', undefined, <LogisticsIcon />, [
            getItem('Импорт', 'import', '/flc/logistics/import', <ImportTruckIcon />),
            getItem('Доставка', 'delivery', '/flc/logistics/delivery', <DeliveryTruckIcon />),
            getItem(
                <>
                    Предзагрузка
                    <Tooltip title='Новые планы погрузок'>
                        <Badge count={newLoadingPlansQty} overflowCount={10} offset={[10, -1]}></Badge>
                    </Tooltip>
                </>,
                'preloading',
                '/flc/logistics/preloading',
                <BuildOutlined />,
            ),
        ]),
        getItem('Склад', 'warehouse', undefined, <WarehouseIcon style={{ fontSize: 16 }} />, [
            getItem('Приемка', 'acceptance', '/flc/warehouse/acceptance', <AcceptanceIcon />),
            getItem('Догруз', 'extraconsignments', '/flc/warehouse/extraconsignments', <CartIcon />),
            getItem('Остатки', 'remainings', '/flc/warehouse/remainings', <BoxesIcon />),
            getItem('Отгрузка', 'shipmentjournals', '/flc/warehouse/shipmentjournals', <LoaderIcon />),
        ]),
    ];

    if (
        hasPermission(userSession.permissions, Permission.ManageTransportBill) ||
        hasPermission(userSession.permissions, Permission.EditTransportBill) ||
        hasPermission(userSession.permissions, Permission.ViewTransportBill) ||
        hasPermission(userSession.permissions, Permission.SetPaid) ||
        hasPermission(userSession.permissions, Permission.ManageTariff) ||
        hasPermission(userSession.permissions, Permission.ManageBalance) ||
        hasPermission(userSession.permissions, Permission.ViewBalance) ||
        hasPermission(userSession.permissions, Permission.ManageWaybill)
    ) {
        const finItems = [];

        if (
            hasPermission(userSession.permissions, Permission.ManageTransportBill) ||
            hasPermission(userSession.permissions, Permission.EditTransportBill) ||
            hasPermission(userSession.permissions, Permission.ViewTransportBill) ||
            hasPermission(userSession.permissions, Permission.SetPaid)
        ) {
            finItems.push(getItem('Счета на оплату', 'bills', '/flc/finance/bills', <BillIcon style={{ fontSize: 16 }} />));
        }

        if (
            hasPermission(userSession.permissions, Permission.ManageBalance) ||
            hasPermission(userSession.permissions, Permission.ViewBalance)
        ) {
            finItems.push(getItem('Балансы', 'balances', '/flc/finance/balances', <AccountingIcon style={{ fontSize: 20 }} />));
        }

        if (hasPermission(userSession.permissions, Permission.ManageTariff)) {
            finItems.push(getItem('Тарифы', 'tariffs', '/flc/finance/tariffs', <TariffIcon />));
        }

        if (hasPermission(userSession.permissions, Permission.ManageWaybill)) {
            finItems.push(getItem('AWB', 'waybills', '/flc/finance/waybills', <FileTextOutlined style={{ fontSize: 16 }} />));
        }

        items.push(getItem('Финансы', 'finances', undefined, <UsdIcon style={{ fontSize: 16 }} />, finItems));
    }

    items.push(getItem('Документы', 'documents', '/flc/documents', <FolderOpenFilled />));

    if (
        hasPermission(userSession.permissions, Permission.ViewUsersStatistic) ||
        hasPermission(userSession.permissions, Permission.ViewImportTrucksStatistic)
    ) {
        const statisticItems = [];

        if (hasPermission(userSession.permissions, Permission.ViewUsersStatistic))
            statisticItems.push(getItem('Клиенты', 'usersstatistic', '/flc/statistics/usersstatistic', <TeamOutlined />));

        if (hasPermission(userSession.permissions, Permission.ViewImportTrucksStatistic))
            statisticItems.push(getItem('Импорт', 'importstatistic', '/flc/statistics/importstatistic', <ImportTruckIcon />));

        items.push(getItem('Статистика', 'statistics', undefined, <LineChartOutlined />, statisticItems));
    }

    items.push(
        getItem(
            <>
                Отзывы
                <Badge count={activeFeedbacksQty} overflowCount={100} offset={[10, -1]} color='gold'></Badge>
            </>,
            'feedbacks',
            '/flc/feedbacks',
            <CustomerRateIcon />,
        ),
        getItem('Уведомления', 'notifications', undefined, <NotificationFilled />, [
            getItem('Новое сообщение', 'newnotification', '/flc/notifications/newnotification', <FormOutlined style={{ fontSize: 16 }} />),
            getItem('Push', 'push', '/flc/notifications/push', <MobileOutlined style={{ fontSize: 16 }} />),
            getItem('Emails', 'emails', '/flc/notifications/emails', <EmailIcon style={{ fontSize: 16 }} />),
            getItem('Telegram', 'telegram', '/flc/notifications/telegram', <TelegramIcon style={{ fontSize: 16 }} />),
            getItem('Max', 'max', '/flc/notifications/max', <MaxAppIcon style={{ fontSize: 16 }} />),
            getItem('Sms', 'sms', '/flc/notifications/sms', <MessageOutlined style={{ fontSize: 16 }} />),
        ]),
    );

    if (viewSetup) {
        items.push(
            getItem('Управление', 'settings', undefined, <SettingFilled />, [
                getItem('Грузополучатели', 'consignees', '/flc/settings/consignees'),
                getItem('Пользователи', 'users', '/flc/settings/users'),
                getItem('Контрагенты', 'counterparties', '/flc/settings/counterparties'),
                getItem('Теги', 'tags', '/flc/settings/tags'),
                getItem('Города', 'cities', '/flc/settings/cities'),
                getItem('Транспортные тарифы', 'transporttariffs', '/flc/settings/transporttariffs'),
                getItem('Транспортные перевозчики', 'cargocarriers', '/flc/settings/cargocarriers'),
                getItem('Торговые площадки', 'tradingplatforms', '/flc/settings/tradingplatforms'),
                getItem('Страны', 'countries', '/flc/settings/countries'),
                getItem('Продукты', 'items', '/flc/settings/items'),
                getItem('Упаковка', 'packagetemplates', '/flc/settings/packagetemplates'),
                getItem('Маршрутные точки', 'waypoints', '/flc/settings/waypoints'),
                getItem('Склады', 'warehouses', '/flc/settings/warehouses'),
                getItem('Расчетные счета', 'accounts', '/flc/settings/accounts'),
                getItem('Долги', 'debts', '/flc/settings/debts'),
                getItem('Компании', 'companies', '/flc/settings/companies'),
                getItem('Контакты', 'contacts', '/flc/settings/contacts'),
            ]),
        );
    }

    if (viewAudit) {
        items.push(getItem('Аудит логи', 'auditlogs', '/flc/auditlogs', <AuditReportIcon />));
    }

    const onMenuClick = (e: any) => {
        d(setCurrentMenuItem(e.key));
    };

    const userMenuItems = [
        getItem(
            <div className='user' style={{ cursor: 'pointer', textAlign: 'center', minWidth: 200 }}>
                {userSession.fullName}
            </div>,
            'account',
            undefined,
            undefined,
            [getItem('Личный кабинет', 'useraccount', '/flc/useraccount', undefined)],
        ),
    ];

    const getLogo = () => {
        return siderCollapsed ? (
            <div className='logo'>
                <MiniLogoIcon style={{ fontSize: 30, display: 'table-cell', verticalAlign: 'middle' }} />
            </div>
        ) : (
            <div className='logo'>
                <div className='sub-logo'>
                    <div style={{ marginTop: 5 }}>Срезка</div>
                </div>
                <MainLogoIcon style={{ fontSize: 38, display: 'table-cell', verticalAlign: 'middle' }} />
            </div>
        );
    };

    return (
        <Layout style={{ height: '100vh', minHeight: '100vh' }}>
            <Sider
                collapsible={!isMobile}
                collapsed={siderCollapsed}
                onCollapse={(e) => setSiderCollapsed(e)}
                width={250}
                style={{
                    overflow: 'auto',
                    height: '100vh',
                    zIndex: 100,
                }}
            >
                {getLogo()}
                <Flex style={{ height: 'calc(100vh - 120px)' }} vertical justify='space-between'>
                    <Menu
                        inlineIndent={12}
                        defaultOpenKeys={[defaultOpenKey]}
                        selectedKeys={[currentMenuItem]}
                        mode='inline'
                        items={items}
                        onClick={onMenuClick}
                    />
                    <div className='app-version'>Версия {appVersion}</div>
                </Flex>
            </Sider>
            <Layout>
                <Header className='main-header'>
                    <Flex align='center' justify='flex-end' gap='small'>
                        {isNewYearAroundUs() && <Garland />}
                        <Flex align='center' style={{ background: '#ffffff', position: 'relative' }}>
                            <Menu items={userMenuItems} mode='horizontal' />
                            <Button variant='link' size='large' color='blue' className='logout' onClick={() => onLogout()}>
                                Выход
                            </Button>
                        </Flex>
                    </Flex>
                </Header>
                <Content className='main-content'>
                    <Outlet />
                </Content>
            </Layout>
        </Layout>
    );
};

export default MainLayout;
